<template>
  <div>
    <div class="columns">
      <div class="column">
        <div class="document person--box-shadow">
          <es-loader
            v-if="loading"
            style="margin: 6.5rem"
          />
          <es-exception
            v-else-if="loadingError"
            exceptionMessage="There was an issue. Please try again or contact your administrator."
            style="padding: 6.5rem; margin: 0"
          />
          <template v-else>
            <new-match-message
              v-if="displayNewMatchMessage"
              @newMatch="displayNewMatchMessage = false; loadProcessControl()"
              style="padding: 1rem 2rem"
            >
              <template v-slot:message>
                {{ batchOrTransaction }} submitted. Review details below.
              </template>
              <template v-slot:button-text>
                New {{ properties['BUTTON_LABEL-ASSIGN_EXISTING'] || 'Match' }}
              </template>
            </new-match-message>
            <template v-else>
              <es-form :padding="{ padding: '1.15rem 1.15rem 0 1.15rem' }">
                <template #es-form>
                  <div class="row removeUnderline">
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          Match Option
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <div class="buttons has-addons">
                              <router-link
                                :to="`/directMatch/matchOptions/person/${option.route}`"
                                v-for="(option, index) in activeMatchOptions"
                                :key="index"
                                class="button button--form"
                                replace
                                :style="matchOptionWidth"
                              >
                                {{ option.description }}
                              </router-link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </es-form>
              <router-view
                name="matchOptionsPersonForm"
                :process-control="processControl"
                :personType="personType"
                :sortedPersonTypes="sortedPersonTypes"
                :dmPersonTypeMatching="dmPersonTypeMatching"
                @batchResponse="batches = $event; transactions = []; displayNewMatchMessage = true;"
                @transResponse="transactions = $event; batches = []; displayNewMatchMessage = true;"
                />
              </template>
            </template>
          </div>
        </div>
      </div>
      <div class="columns">
        <div class="column">
          <router-view
          name="matchOptionsSearchResults"
          :batches="batches"
          :transactions="transactions"
          :userPersonTypesCodes="userPersonTypesCodes"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import axios from 'axios';
import EsLoader from '@/components/EsLoader.vue';
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import NewMatchMessage from '@/components/NewMatchMessage.vue';
import PermissionManager from '@/mixins/PermissionManager';

export default {
  mixins: [
    PermissionManager,
  ],
  components: {
    EsLoader,
    EsException,
    EsForm,
    NewMatchMessage,
  },
  data() {
    return {
      allSubmissionSpecs: [],
      batches: [],
      displayNewMatchMessage: null, // Boolean value to display new-match-message component
      loading: true,
      loadingError: null,
      matchOptions: [],
      processControl: {}, // object for storing process-control response
      transactions: [],
      userPersonTypesCodes: null,
    };
  },
  created() {
    this.onLoad();
    this.setDataElements();
  },
  beforeDestroy() {
    this.$root.$off('setLoadingError', this.loadingError);
  },
  mounted() {
    const setLoadingError = (value) => {
      this.loadingError = value;
    };
    this.$root.$on('setLoadingError', setLoadingError);
  },
  destroyed() {
    this.$root.$off('setLoadingError');
  },
  watch: {
    $route: 'selectTab',
  },
  computed: {
    ...mapGetters(['userPersonTypes']),
    ...mapGetters('propertiesModule', [
      'properties',
      'getProperty',
    ]),
    ...mapGetters('lookups', [
      'defaultPersonType',
    ]),
    activeMatchOptions() {
      return this.matchOptions.filter(item => item.isVisible);
    },
    availablePersonTypes() {
      return this.$store.state.user.userPersonTypes
        .map(item => item.personType);
    },
    batchOrTransaction() {
      // return display message for new-match-message component slot.
      if (this.batches.length) {
        return 'Batch';
      }
      return 'Transaction';
    },
    allSubSpecsArray() {
      return [].concat(...this.allSubmissionSpecs);
    },
    enabledPersonTypes() {
      if (this.allSubSpecsArray) {
        return this.allSubSpecsArray
          .filter(d => d.purpose === 'DMATCH' && d.enabled === true)
          .map(p => p.personType.code);
      } return null;
    },
    filteredEnabledPersonTypes() {
      return this.availablePersonTypes
        .filter(p => this.enabledPersonTypes.includes(p.code));
    },
    dmPersonTypeMatching() {
      return JSON.parse(this.getProperty('MATCHING_FEATURES-FEATURE_DM_PERSONTYPE_MATCHING'));
    },
    hasIndividualPermission() {
      return this.hasModule('DRMCH:MATCH:OPTIONS:INDIVIDUALMATCH');
    },
    hasManualPermission() {
      return this.hasModule('DRMCH:MATCH:OPTIONS:MANUALMATCH');
    },
    hasPersonPermission() {
      return this.hasModule('DRMCH:MATCH:OPTIONS:PERSONID');
    },
    hasUploadPermission() {
      return this.hasModule('DRMCH:MATCH:OPTIONS:UPLOADFILE');
    },
    matchOptionWidth() {
      let width;
      if (!this.hasIndividualPermission && !this.hasManualPermission) {
        width = '100px';
      } else if (this.hasIndividualPermission && !this.hasManualPermission) {
        width = '150px';
      } else {
        width = '200px';
      }
      return `min-width: ${width}`;
    },
    personType() {
      if (this.userPersonTypesCodes.includes(this.defaultPersonType)) {
        return this.defaultPersonType;
      } return this.userPersonTypesCodes[0];
    },
    sortedPersonTypes() {
      return this.filteredEnabledPersonTypes.slice().sort((a, b) => (a.name > b.name ? 1 : -1));
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadMatchTypes',
      'loadPersonTypes',
      'loadGenders',
      'loadStates',
      'loadSources',
      'loadGrades',
      'loadRaces',
    ]),
    loadProcessControl() {
      return axios.get('process-control', { params: { type: 'DM_PROGRAM_VS_ALL_STUDENTS' } });
    },
    async loadSubmissionSpecs() {
      const personTypeIds = this.availablePersonTypes.map(item => item.id);
      // build promises to get submission specs info per person type
      const submissionSpecResponse = await Promise.all([...personTypeIds]
        .map(id => axios.get(`person-types/${id}/submission-specs/`)));
        // use the response from above and push them
      submissionSpecResponse.forEach((s) => {
        this.allSubmissionSpecs.push(s.data);
      });
    },
    async onLoad() {
      try {
        this.loading = true;
        this.loadingError = false;
        // if access to Person ID tab, check process-control
        if (this.hasPersonPermission) {
          const res = await this.loadProcessControl();
          if (res.status === 200) {
            this.processControl = res.data;
          } else {
            this.processControl = { status: 'DONE' };
          }
        }
        // load lookups
        await Promise.all([
          this.loadMatchTypes(),
          this.loadPersonTypes(),
          this.loadGenders(),
          this.loadStates(),
          this.loadSources(),
          this.loadGrades(),
          this.loadRaces(),
        ]);
        this.loadSubmissionSpecs();
      } catch (e) {
        console.log(e);
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    setDataElements() {
      this.userPersonTypesCodes = this.userPersonTypes
        .map(item => item.code);
      this.matchOptions = [
        {
          description: 'Person ID',
          route: 'person',
          isVisible: this.hasPersonPermission,
        },
        {
          description: 'Upload File',
          route: 'upload',
          isVisible: this.hasUploadPermission,
        },
        {
          description: 'Individual Match',
          route: 'individual',
          isVisible: this.hasIndividualPermission,
        },
        {
          description: 'Manual Authorization',
          route: 'manual',
          isVisible: this.hasManualPermission,
        },
      ];
    },
    selectTab() {
      if (this.$route.name === 'dmMatchOptionsPerson') {
        if (this.hasModule('DRMCH:MATCH:OPTIONS:PERSONID')) {
          this.$router.replace({ name: 'dmMatchOptionsPersonPersonId' });
        } else if (this.hasModule('DRMCH:MATCH:OPTIONS:UPLOADFILE')) {
          this.$router.replace({ name: 'dmMatchOptionsPersonUpload' });
        } else if (this.hasModule('DRMCH:MATCH:OPTIONS:INDIVIDUALMATCH')) {
          this.$router.replace({ name: 'dmMatchOptionsPersonIndividual' });
        } else if (this.hasModule('DRMCH:MATCH:OPTIONS:MANUALMATCH')) {
          this.$router.replace({ name: 'dmMatchOptionsPersonManual' });
        }
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  .person--box-shadow
    box-shadow: 0px 3px 3px #aaa
</style>
